import { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import debug from "@plannel/utils/debug-util";
import { groupBy, includes, isEmpty, isEqual, keys, mapValues, omit, size, sortBy, cloneDeep } from "lodash";

import { Box, Tab } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import BaseConfigModal, { CONFIG_TAB_SX } from "@plannel/components/modal/BaseConfigModal";
import StatPill from "@plannel/components/ui/StatPill";
import TokenSelectGroup from "@plannel/components/ui/TokenSelectGroup";
import codeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import p13nService from "@plannel/services/system/p13n-service";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import { FS } from "@plannel/utils/font-util";

/*
 * 본문은 섹션별 토큰(알약) 그리드다 — 체크박스 목록이 아니다.
 * 계층 레벨 4 + Code + ABC-XYZ + 속성 10 을 체크박스 행으로 세우면 모달이 세로로 길어져
 * 속성을 2열로 접어야 했다. 토큰은 라벨 길이만큼만 차지하고 가로로 흘러 접힌다.
 * 전체선택은 섹션 제목 오른쪽의 Select All / Clear All 링크가 맡는다(옛 active 헤더 자리).
 */

const MultiHrchyTabContext = ({ t, configData, setSelectedTab, selectedTab, dimendData, setDimendData, attributes, selectLvCd, attrConfig, item, customer, dimenKey, hasLevel, attrTarget }) => {
  const bottomLevel = [item.cd, customer.cd];
  // 속성·Code 는 최하위 레벨(품목/거래처)에서만 편집한다 — 상위 계층 레벨엔 붙일 속성이 없다
  const isBottomLevel = includes(bottomLevel, selectLvCd);

  const handleChange = (_event, newValue) => {
    setSelectedTab(newValue);
  };

  // 탭(계층 그룹) 하나의 저장 형식은 { attr, hasCode, hasAbcXyz, configObj } 다.
  // 한 키만 고쳐도 나머지 세 키를 채워 보낸다 — Workbench 가 네 키를 모두 읽는다.
  const setTabData = patch => {
    const current = dimendData[selectedTab] ?? {};
    setDimendData({
      ...dimendData,
      [selectedTab]: {
        attr: current.attr ?? [],
        hasCode: current.hasCode ?? false,
        hasAbcXyz: current.hasAbcXyz ?? false,
        configObj: current.configObj ?? [],
        ...patch,
      },
    });
  };

  /*
   * 계층 레벨 — 저장값은 cd 가 아니라 계층 설정 객체 통째다(Workbench 가 seqNum/name 으로 컬럼을 만든다).
   * 이미 담긴 객체는 그대로 두고 새로 켜진 것만 configData 에서 찾아 붙인다.
   * cd 는 그룹끼리 겹칠 수 있어 지금 탭(그룹) 안에서만 찾는다.
   */
  const handleLvChange = nextCds => {
    const current = dimendData[selectedTab]?.configObj ?? [];
    const kept = current.filter(config => includes(nextCds, config.cd));
    const added = nextCds
      .filter(cd => !current.some(config => isEqual(config.cd, cd)))
      .map(cd => configData.find(config => isEqual(config.grpCd, selectedTab) && isEqual(config.cd, cd)))
      .filter(config => !isEmpty(config))
      // updatedTs/createdTs 는 p13n 에 남기지 않는다
      .map(config => omit(config, ["updatedTs", "createdTs"]));

    /*
     * 최하위(Item/Customer) 레벨은 조회 그룹과 무관하게 저장된 그룹의 상위 계층을 그리드에 붙이는데,
     * 서로 다른 그룹 트리의 상위 계층이 한 그리드에 섞이면 안 되므로 한 번에 한 그룹만 고르게 한다.
     * 이 탭에 레벨을 새로 켜면 다른 그룹 탭의 레벨 선택만 해제한다(Code·속성·ABC-XYZ 는 그대로).
     */
    const otherTabs = isBottomLevel && size(added) > 0
      ? mapValues(omit(dimendData, [selectedTab]), tabData => ({ ...tabData, configObj: [] }))
      : {};
    const currentTab = dimendData[selectedTab] ?? {};
    setDimendData({
      ...dimendData,
      ...otherTabs,
      [selectedTab]: {
        attr: currentTab.attr ?? [],
        hasCode: currentTab.hasCode ?? false,
        hasAbcXyz: currentTab.hasAbcXyz ?? false,
        configObj: [...kept, ...added],
      },
    });
  };

  // 속성은 저장 시 정렬한다 — attr01~attr10 순서가 컬럼 순서다
  const handleAttrChange = nextAttrs => {
    const attr = [...nextAttrs].sort();
    if (hasLevel) {
      setTabData({ attr });
    } else {
      setDimendData({ attr });
    }
  };

  // 레벨 선택이 없는 화면(RP/MP Review, PSI Simulation)의 customer 속성 — item 과 같은 형태지만 custAttr 에 저장
  const handleCustAttrChange = nextAttrs => {
    setDimendData({ ...dimendData, custAttr: [...nextAttrs].sort() });
  };

  // 속성 라벨은 회사별 이름(attrConfig)이 있으면 그걸 쓰고 없으면 번역 키로 떨어진다
  const attrOptions = configKey => attributes.map(attr => ({
    value: attr,
    label: attrConfig?.[configKey]?.[attr] ?? t(attr),
  }));

  const groupConfig = configData ? groupBy(configData, "grpCd") : {};
  if (size(groupConfig) > 0) {
    return (
      <TabContext value={selectedTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList
            onChange={handleChange}
            scrollButtons="auto"
            variant="scrollable"
            sx={CONFIG_TAB_SX}>
            {keys(groupConfig)?.map(grpKey => {
              // 최하위 레벨 — 그리드에 상위 계층이 붙을 그룹(레벨을 고른 탭)에 점을 찍는다
              const hasLevels = isBottomLevel && size(dimendData[grpKey]?.configObj) > 0;
              return (
                <Tab
                  key={groupConfig[grpKey][0].grpCd}
                  value={groupConfig[grpKey][0].grpCd}
                  label={
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                      {groupConfig[grpKey][0].grpName}
                      {hasLevels ? <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main" }} /> : null}
                    </Box>
                  }
                />
              );
            })}
          </TabList>
        </Box>
        {isBottomLevel ? (
          <Box sx={{ pt: 1.5, fontSize: FS.CAPTION, color: "text.secondary" }}>
            {t("MSG_BottomLevelSingleHrchyGroup")}
          </Box>
        ) : null}
        <Box sx={{ overflow: "auto", maxHeight: "60vh", minWidth: 250 }}>
          {keys(groupConfig).map(grpKey => {
            const tabData = dimendData[grpKey] ?? {};
            // seqNum 0 은 최상위(전사) 가상 레벨이라 고르는 대상이 아니다
            const levelOptions = sortBy(groupConfig[grpKey], "seqNum")
              .filter(config => config.seqNum !== 0)
              .map(config => ({ value: config.cd, label: config.name }));

            return (
              <TabPanel
                key={groupConfig[grpKey][0].grpCd}
                value={groupConfig[grpKey][0].grpCd}
                sx={{ px: 0, py: 2 }}>
                <TokenSelectGroup
                  label={t("hrchyLv")}
                  options={levelOptions}
                  value={(tabData.configObj ?? []).map(config => config.cd)}
                  onChange={handleLvChange}
                />
                {isBottomLevel ? (
                  <TokenSelectGroup
                    label={t("code")}
                    showSelectAll={false}
                    options={[{ value: "hasCode", label: t("codeVisible") }]}
                    value={tabData.hasCode ? ["hasCode"] : []}
                    onChange={next => setTabData({ hasCode: next.length > 0 })}
                  />
                ) : null}
                {isEqual(selectLvCd, item.cd) ? (
                  <TokenSelectGroup
                    label={t("abcXyzClassification")}
                    showSelectAll={false}
                    options={[{ value: "hasAbcXyz", label: t("invClassCd") }]}
                    value={tabData.hasAbcXyz ? ["hasAbcXyz"] : []}
                    onChange={next => setTabData({ hasAbcXyz: next.length > 0 })}
                  />
                ) : null}
                <TokenSelectGroup
                  label={t("attr")}
                  disabled={!isBottomLevel}
                  options={attrOptions(dimenKey)}
                  value={isBottomLevel ? tabData.attr ?? [] : []}
                  onChange={handleAttrChange}
                  sx={{ mb: 0 }}
                />
              </TabPanel>
            );
          })}
        </Box>
      </TabContext>
    );
  } else if (!hasLevel) {
    const isCustTarget = isEqual(attrTarget, "customer");
    return (
      <TokenSelectGroup
        label={t("attr")}
        options={attrOptions(isCustTarget ? "customer" : "item")}
        value={(isCustTarget ? dimendData?.custAttr : dimendData?.attr) ?? []}
        onChange={isCustTarget ? handleCustAttrChange : handleAttrChange}
        sx={{ mb: 0 }}
      />
    );
  }
  return null;
};

const DimensAttrModal = props => {
  const { t, close, open, selectLv, setDimensionAttrData, dimensionAttrData, attrConfig, viewName, attrTarget } = props;
  const hasLevel = !isEmpty(selectLv);
  const { selectLvCd, isCust, selectGrpCd } = selectLv;

  const { attributes, dpP13N: { DP_WORKBENCH_ETC: pageCd }, customer, item } = codeUtil;
  const { snackBarMessageColor } = ColorUtil;
  const dimenKey = isCust ? (isEqual(selectLvCd, customer.cd) ? "customer" : "custHrchy") : (isEqual(selectLvCd, item.cd) ? "item" : "itemHrchy");
  // 레벨 선택이 없는 화면(RP/MP Review, PSI Simulation)은 기본적으로 item 속성만 다루고, attrTarget 을 넘기는 화면(Channel Planning Review)은 클릭한 컬럼(item/customer)의 속성만 단독으로 다룬다
  const dimenKeyNm = hasLevel ? isEqual(dimenKey, "custHrchy") ? t("customerHierarchy") : t(dimenKey) : t(isEqual(attrTarget, "customer") ? "customer" : "item");

  const [multiHrchyData, setMultiHrchyData] = useState([]);
  const [dimendData, setDimendData] = useState({});
  const [selectedTab, setSelectedTab] = useState(null);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  // 제목 옆 선택 개수 — 탭 화면은 지금 보고 있는 계층 그룹 기준이다
  const activeTabData = hasLevel ? dimendData[selectedTab] : null;
  const selectedCount = hasLevel
    ? size(activeTabData?.configObj) + size(activeTabData?.attr)
      + (activeTabData?.hasCode ? 1 : 0) + (activeTabData?.hasAbcXyz ? 1 : 0)
    : size(isEqual(attrTarget, "customer") ? dimendData?.custAttr : dimendData?.attr);

  const saveP13nAttribute = () => {
    const configObj = { ATTRIBUTE: dimensionAttrData };
    if (size(dimendData) > 0) {
      // If there are multiple hierarchy configurations, save them under the selected tab
      hasLevel ? configObj.ATTRIBUTE[dimenKey] = dimendData : configObj.ATTRIBUTE = dimendData;
      const savePageCd = viewName ?? pageCd ;
      const params = { pageCd: savePageCd, configObj };

      p13nService.upsert(params)
        .then(response => {
          if (response.status === 200) {
            setDimensionAttrData(response.data?.configObj?.ATTRIBUTE);
          }
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
        .finally(() => {
          close(false);
        });
    }
  };

  const handleSave = () => {
    saveP13nAttribute();
  };

  const handleClose = () => {
    close();
    setMultiHrchyData([]);
    setDimendData({});
    setSelectedTab(null);
  };

  const handleDialog = action => {
    if (action === "CANCEL") {
      handleClose();
    }
  };

  const retrieveHrchyConfigs = () => {
    hrchyConfigService.getAllForCSV()
      .then(response => {
        const { results } = response.data;
        if (results) {
          const configs = results.filter(result => (isCust ? !result.itemHrchyFlg : result.itemHrchyFlg));
          if (size(configs) > 0) {
            setMultiHrchyData(configs);
          }
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  useEffect(() => {
    if (open) {
      if (hasLevel) {
        retrieveHrchyConfigs();
      } else {
        setDimendData(!isEmpty(dimensionAttrData) ? cloneDeep(dimensionAttrData) : {});
      }
    } else {
      setSnackInfo({ open: false, severity: "", content: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (size(multiHrchyData) > 0) {
      setSelectedTab(isEmpty(selectGrpCd) ? multiHrchyData[0].grpCd : selectGrpCd);
      setDimendData(size(dimensionAttrData) > 0 ? cloneDeep(dimensionAttrData[dimenKey] ?? {}) : {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiHrchyData]);

  return (
    <>
      <BaseConfigModal
        open={open}
        onClose={handleClose}
        title={`${dimenKeyNm} ${t("dimensionSetting")}`}
        titleAdornment={<StatPill component="span" label={t("selected")} value={selectedCount} accent />}
        subtitle={t("MSG_SelectItemsToShow")}
        onSave={handleSave}
        /*
         * 레벨이 없는 화면(RP/MP Review, PSI Simulation)은 본문이 속성 토큰 한 줄뿐이라 lg 면 우측이 텅 빈다.
         * 제목도 md 에 들어간다 — 최장이 vi "Mặt Hàng Thiết Lập Tham Số"(26자 ≈ 273px) + 패딩 40px = 313px.
         * 탭 분기(DP Workbench)는 계층 레벨 목록·Code·ABC 까지 들어가므로 lg 를 유지한다.
         */
        size={hasLevel ? "lg" : "md"}>
        {selectedTab || !hasLevel ? (
          <MultiHrchyTabContext
            configData={multiHrchyData}
            t={t}
            hasLevel={hasLevel}
            setSelectedTab={setSelectedTab}
            selectedTab={selectedTab}
            dimendData={dimendData}
            setDimendData={setDimendData}
            attributes={attributes}
            selectLvCd={selectLvCd}
            attrConfig={attrConfig}
            item={item}
            customer={customer}
            dimenKey={dimenKey}
            attrTarget={attrTarget}
          />
        ) : null}
      </BaseConfigModal>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        actionName={dialogInfo.actionName}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(DimensAttrModal);
